import {
  getLabelPaddingsValues, initialLabelVisualizerBodyProps,
} from '~/_shared/components/labelVisualizer/labelVisualizer.constants';
import { type Theme } from '~/_shared/themes/theme.model';
import { type LatLng } from '~/_shared/types/latLng';
import {
  MarkerSegment, type MarkerStyle, MarkerStyleType, type MarkerVisualSettings,
} from '~/_shared/types/marker.types';
import {
  type LabelVisualSetting, type MarkerSettings,
} from '~/_shared/types/markers/visualSettings.types';
import { type SpreadsheetRowId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { copy } from '~/_shared/utils/collections/collections';
import { convertColorToWebGLColor } from '~/_shared/utils/colors/colors.helpers';
import { getLabelStroke } from '~/_shared/utils/labels/labels.helpers';
import {
  createLabelWithBodyVirtualLayer, createWebglOverlayLabelWithBody, type LabelWithBodyVirtualLayer,
  type WebglOverlayLabelWithBody,
} from '~/_shared/utils/webgl/labelWithBody';
import {
  getDirectionsTileColors, getDirectionsTilePosition,
} from '~/directions/panel/input/directionsPanelInput.helpers';
import {
  type RouteUiData, type Waypoint,
} from '~/store/frontendState/mapTools/directions/directions.state';
import { type MapSettingsRoute } from '~/store/mapSettings/directions/mapSettingsDirections.state';
import { type DirectionsWaypointZIndexSubEntity } from '../../zIndexes/useDirectionsRouteZIndexes.hook';
import { createWebGLMarker } from '../markers/manager/mapMarkerManager';
import { type ExtendsWebglLayers } from '../webgl/useWebGL';

const DIRECTIONS_COLLAPSED_MARKER_SIZE = 25;

type RenderedRouteData = Readonly<{
  renderers: ReadonlyArray<google.maps.DirectionsRenderer>;
  color: string;
  labels: ReadonlyArray<WebglOverlayLabelWithBody>;
  markers: ReadonlyArray<WebglOverlayMarker>;
}>;

type RequiredLayers = ExtendsWebglLayers<{
  MarkerLabelsText: LabelsLayer;
  MarkerLabelsTextBoxBackground: TextBoxCalloutLayer;
  MapObjectMarkers: MarkersLayer;
}>;

type MouseEventCallbacks = Readonly<{
  onClick: (markerId: SpreadsheetRowId) => void;
  onMouseOut: (markerId: SpreadsheetRowId) => void;
  onMouseOver: (markerId: SpreadsheetRowId) => void;
}>;

const labelTriangleWidth = 3.5;
const labelTriangleHeight = 3.5;

const getWaypointsColors = (props: {
  index: number; totalWaypoints: number; theme: Theme; routeColor: string;
}) => {
  const position = getDirectionsTilePosition(props.index, props.totalWaypoints);
  const routeColors = getDirectionsTileColors(position, props.theme, props.routeColor);

  return {
    background: routeColors.background,
    text: routeColors.textColor,
  };
};

export class MapDirectionsManager {
  private readonly map: google.maps.Map;
  private readonly labelsVirtualLayer: LabelWithBodyVirtualLayer;
  private readonly markersLayer: MarkersLayer;
  private renderedRoutes: ReadonlyMap<string, RenderedRouteData> = new Map();

  constructor(map: google.maps.Map, layers: RequiredLayers) {
    this.map = map;
    this.labelsVirtualLayer = createLabelWithBodyVirtualLayer(layers.MarkerLabelsText, layers.MarkerLabelsTextBoxBackground);
    this.markersLayer = layers.MapObjectMarkers;
  }

  renderRoute(
    route: MapSettingsRoute,
    routeUiData: RouteUiData,
    mouseEventCallbacks: MouseEventCallbacks,
    waypointZIndexes: ReadonlyArray<{ [key in DirectionsWaypointZIndexSubEntity]: number }>,
    isDraggable: boolean,
    onRouteChange: (routeId: string, routes: ReadonlyArray<google.maps.DirectionsRoute>) => void,
    theme: Theme,
    markerStyle?: MarkerStyle,
  ) {
    const renderers = routeUiData.apiResponses.map(directions => {
      const directionsRenderer = new google.maps.DirectionsRenderer({
        map: this.map,
        suppressMarkers: false,
        markerOptions: { visible: false },
        polylineOptions: { strokeColor: route.color, strokeWeight: 4 },
        draggable: isDraggable,
        preserveViewport: true,
        directions,
      });

      directionsRenderer.addListener('directions_changed', () => {
        const newDirections = directionsRenderer.getDirections();

        if (!newDirections) {
          return;
        }

        onRouteChange(route.id, newDirections.routes);
      });

      return directionsRenderer;
    });

    if (this.renderedRoutes.has(route.id)) {
      this.removeRoute(route.id);
    }

    const lastWaypointIndex = route.waypoints.length - 1;
    const { markers, labels } = route.waypoints.reduce<{
      markers: ReadonlyArray<WebglOverlayMarker>; labels: ReadonlyArray<WebglOverlayLabelWithBody>;
    }>((acc, waypoint, index) => {
      const routeDataLatLng = index === lastWaypointIndex
        ? routeUiData.legs[index - 1]?.end_location
        : routeUiData.legs[index]?.start_location;

      const latLng = waypoint.latLng || (routeDataLatLng ? { lat: routeDataLatLng.lat(), lng: routeDataLatLng.lng() } : null);

      if (!latLng) {
        return acc;
      }

      const zIndex = waypointZIndexes[index] || { text: 0, background: 0 };

      if (markerStyle) {
        const marker = createMarker(
          index,
          route.waypoints.length,
          theme,
          latLng,
          zIndex,
          route.color,
          markerStyle,
        );
        addEventListenersToMapObject(waypoint, marker, mouseEventCallbacks);
        this.markersLayer.add(marker);
        return { ...acc, markers: [...acc.markers, marker] };
      }
      else {
        const label = createLabel(
          latLng,
          index,
          route.waypoints.length,
          zIndex,
          theme,
          route.color
        );

        addEventListenersToMapObject(waypoint, label.textBoxCallout, mouseEventCallbacks);
        this.labelsVirtualLayer.add(label);
        return { ...acc, labels: [...acc.labels, label] };
      }
    }, { markers: [], labels: [] });

    this.renderedRoutes = copy.andAdd(
      this.renderedRoutes,
      [[route.id, { renderers, color: route.color, labels, markers }]],
      (newValue, oldValue) => {
        oldValue.renderers.forEach(removeRendererFromMap);
        return newValue;
      });
  }

  removeRoute(routeId: string) {
    this.renderedRoutes.get(routeId)?.renderers.forEach(removeRendererFromMap);
    this.renderedRoutes.get(routeId)?.labels.forEach(label => this.labelsVirtualLayer.remove(label));
    this.renderedRoutes.get(routeId)?.markers.forEach(marker => this.markersLayer.remove(marker));
    this.renderedRoutes = copy.andRemove(this.renderedRoutes, [routeId]);
  }

  destroy() {
    this.renderedRoutes.forEach((_, key) => this.removeRoute(key));
  }
}

const removeRendererFromMap = (renderer: google.maps.DirectionsRenderer) => renderer.setMap(null);

const createLabel = (
  latLng: LatLng,
  index: number,
  totalWaypoints: number,
  zIndex: { [key in DirectionsWaypointZIndexSubEntity]: number },
  theme: Theme,
  routeColor: string
): WebglOverlayLabelWithBody => {
  const labelSettings = getDirectionsLabelMarkerSettings({ index, totalWaypoints, theme, routeColor }).label;
  const paddings = getLabelPaddingsValues(labelSettings.bodyProps);
  const stroke = getLabelStroke(labelSettings.bodyProps);

  return createWebglOverlayLabelWithBody({
    lat: latLng.lat,
    lng: latLng.lng,
    text: {
      value: (index + 1).toString(),
      strokeWidth: stroke.strokeWidth,
      strokeColor: stroke.strokeColor,
      fillColor: convertColorToWebGLColor(labelSettings.bodyProps.fontColor.selectedColor),
      fontSize: labelSettings.bodyProps.fontSize,
    },
    borderColor: convertColorToWebGLColor(labelSettings.borderProps.color.selectedColor),
    borderWidth: labelSettings.borderProps.width,
    borderRadius: labelSettings.borderProps.radius,
    fillColor: convertColorToWebGLColor(labelSettings.bodyProps.backgroundColor.selectedColor),
    triangle: true,
    triangleWidth: labelSettings.arrowProps.dimensions.width,
    triangleHeight: labelSettings.arrowProps.dimensions.height,
    offset: { y: -(labelSettings.arrowProps.dimensions.height || 0), x: 0 },
    padding: {
      t: paddings.top,
      r: paddings.right,
      l: paddings.left,
      b: paddings.bottom,
    },
    autoHideWhenCollide: false,
  }, zIndex);
};

const createMarker = (
  index: number,
  totalWaypoints: number,
  theme: Theme,
  latLng: LatLng,
  zIndex: { [key in DirectionsWaypointZIndexSubEntity]: number },
  routeColor: string,
  markerStyle: MarkerStyle,
): WebglOverlayMarker => {
  const waypointsColors = getWaypointsColors({ index, totalWaypoints, theme, routeColor });
  const backgroundWebGLColor = convertColorToWebGLColor(waypointsColors.background);
  return createWebGLMarker({
    latLngData: latLng,
    zIndex: zIndex.background,
    style: markerStyle,
    color: { color: backgroundWebGLColor, opacity: 1, backgroundOpacity: 1 },
    segment: MarkerSegment.MAIN,
    isHidden: false,
    size: DIRECTIONS_COLLAPSED_MARKER_SIZE,
  });
};

const addEventListenersToMapObject = (
  waypoint: Waypoint,
  object: WebglOverlayMarker | WebglOverlayTextBoxCallout | null,
  mouseEventCallbacks: MouseEventCallbacks,
) => {
  const markerId = waypoint.markerId;
  if (object && markerId) {
    object.addEventListener('click', () => mouseEventCallbacks.onClick(markerId));
    object.addEventListener('mouseout', () => mouseEventCallbacks.onMouseOut(markerId));
    object.addEventListener('mouseover', () => mouseEventCallbacks.onMouseOver(markerId));
  }
};

export const getDirectionsWaypointMarkerSettings = (props: {
  isCollapsed: boolean;
  index: number;
  totalWaypoints: number;
  theme: Theme;
  routeColor: string;
}): Readonly<MarkerSettings> => {
  if (props.isCollapsed) {
    return getDirectionsMarkerSettings(props);
  }
  else {
    return getDirectionsLabelMarkerSettings(props);
  }
};

export const getDirectionsLabelMarkerSettings = (props: {
  index: number; totalWaypoints: number; theme: Theme; routeColor: string;
}): Readonly<MarkerSettings & { label: LabelVisualSetting }> => {
  const waypointsColors = getWaypointsColors(props);

  return {
    label: {
      arrowProps: {
        dimensions: { height: labelTriangleHeight, width: labelTriangleWidth },
      },
      bodyProps: {
        backgroundColor: { selectedColor: waypointsColors.background },
        dimensions: {},
        fontColor: { selectedColor: waypointsColors.text },
        fontSize: 17,
        padding: initialLabelVisualizerBodyProps.padding,
      },
      borderProps: {
        color: { selectedColor: '#000' },
        radius: 5,
        width: 0.9,
      },
      useLabel: true,
    },
    useMarker: false,
  };
};

export const getDirectionsMarkerSettings = (props: {
  index: number; totalWaypoints: number; theme: Theme; routeColor: string;
}): Readonly<MarkerSettings & { marker: MarkerVisualSettings }> => {
  const waypointsColors = getWaypointsColors(props);
  return getCollapsedDirectionsMarkerProps({
    color: waypointsColors.background,
  }).markerSettings;
};

export const getCollapsedDirectionsMarkerProps = ({
  color,
}: {
  color: string;
}) => {
  const markersSettings: MarkerVisualSettings = {
    selectedColor: color,
    styleType: MarkerStyleType.STANDARD,
    styleId: 13,
    size: DIRECTIONS_COLLAPSED_MARKER_SIZE,
  };

  return {
    markerSettings: {
      marker: markersSettings,
      useMarker: true,
    } satisfies MarkerSettings,
    maxDimensions: { height: DIRECTIONS_COLLAPSED_MARKER_SIZE, width: DIRECTIONS_COLLAPSED_MARKER_SIZE },
    maxSize: DIRECTIONS_COLLAPSED_MARKER_SIZE,
  };
};
